import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Brain, Search, Trash2 } from "lucide-react";
import { MEMORY_KINDS, MEMORY_SCHEMA_VERSION, MEMORY_ONTOLOGY, RECALL_WEIGHTS, RECALL_TOP_K_DEFAULT } from "../data/generated/memory-schema.generated";
import { getSession } from "../lib/session";
import { listForSession, recall } from "../lib/memory/store";
import { env } from "../lib/env";
import { cls } from "../lib/cls";

export default function AdminMemory(): JSX.Element {
  const session = getSession();
  const userIri = session?.email ?? "anon";
  const sessionIri = useMemo(
    () => sessionStorage.getItem(`${env.VITE_LS_SESSION_ID_PREFIX}${userIri}`) ?? "(no session)",
    [userIri],
  );
  const records = listForSession(userIri, sessionIri);
  const [q, setQ] = useState("");

  const recalled = useMemo(() => {
    if (!q.trim()) return null;
    return recall({ user_iri: userIri, session_iri: sessionIri, query: q.trim() });
  }, [q, userIri, sessionIri]);

  function clearAll(): void {
    if (!confirm("Clear all browser-side memory for this session?")) return;
    localStorage.removeItem("foyrmind.memory.v1");
    window.location.reload();
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
      <div className="panel p-3 lg:col-span-2">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="flex items-center gap-1.5">
            <Brain className="w-4 h-4 text-foyr" />
            <h2 className="display-3 text-ink-950">Memory</h2>
            <span className="pill bg-cream-dark border border-border text-ink-700 text-[10px] tabular">
              {records.length} records
            </span>
            <span className="pill bg-cream-dark border border-border text-ink-700 text-[10px] tabular">
              schema {MEMORY_SCHEMA_VERSION}
            </span>
          </div>
          <button onClick={clearAll} className="btn btn-outline !text-xs !py-1.5">
            <Trash2 className="w-3 h-3" /> Clear (this browser only)
          </button>
        </div>
        <div className="relative mb-3">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Recall: 'what does the client prefer?'…"
            className="w-full bg-cream border border-border rounded pl-8 pr-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-foyr/30"
          />
        </div>
        {recalled ? (
          <div className="space-y-1.5 mb-3">
            {recalled.hits.length === 0 ? (
              <div className="text-[12px] text-ink-500 italic">No memory matched ≥ floor.</div>
            ) : recalled.hits.map((h) => (
              <motion.div
                key={h.record.record_id}
                initial={{ opacity: 0, y: 2 }} animate={{ opacity: 1, y: 0 }}
                className="border border-border rounded p-2 bg-cream-dark/40"
              >
                <div className="flex items-center gap-1 flex-wrap mb-0.5">
                  <span className="pill bg-foyr text-cream text-[10px] tabular">total {h.total.toFixed(3)}</span>
                  <span className="pill bg-cream-dark border border-border text-ink-500 text-[10px]">{h.record.kind_id}</span>
                  <span className="text-[10px] text-ink-500 tabular">
                    sem {h.semantic.toFixed(2)} · temp {h.temporal.toFixed(2)} · kind {h.kind_score.toFixed(2)} · rec {h.recency.toFixed(2)}
                  </span>
                </div>
                <div className="text-[12.5px] text-ink-950">{h.record.content}</div>
              </motion.div>
            ))}
          </div>
        ) : null}
        <div className="text-[10px] uppercase tracking-wider text-ink-500 mb-1">All records</div>
        {records.length === 0 ? (
          <div className="text-[12.5px] text-ink-500 italic">No memory captured yet. Use the cockpit side rail to capture entries.</div>
        ) : (
          <ul className="space-y-1 max-h-[420px] overflow-auto">
            {[...records].reverse().map((r) => (
              <li key={r.record_id} className="border border-border rounded p-2 bg-cream-dark/30">
                <div className="flex items-center gap-1 flex-wrap mb-0.5">
                  <span className={cls("pill text-[10px]", "bg-ink-950 text-cream")}>{r.kind_id}</span>
                  <span className="text-[10px] text-ink-500 tabular">{new Date(r.created_at).toLocaleString()}</span>
                </div>
                <div className="text-[12.5px] text-ink-950">{r.content}</div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <aside className="panel p-3">
        <div className="text-[11px] uppercase tracking-wider text-ink-500 mb-1.5">Recall weights</div>
        <ul className="space-y-1 text-[12px] mb-3">
          {Object.entries(RECALL_WEIGHTS).map(([k, v]) => (
            <li key={k} className="flex justify-between">
              <span className="text-ink-500">{k}</span>
              <span className="font-mono text-ink-950 tabular">{Number(v).toFixed(2)}</span>
            </li>
          ))}
          <li className="flex justify-between border-t border-border pt-1 mt-1">
            <span className="text-ink-500">top-k default</span>
            <span className="font-mono text-ink-950 tabular">{RECALL_TOP_K_DEFAULT}</span>
          </li>
        </ul>
        <div className="text-[11px] uppercase tracking-wider text-ink-500 mb-1.5">Memory kinds</div>
        <ul className="space-y-1.5 text-[11.5px]">
          {MEMORY_KINDS.map((k) => (
            <li key={k.id} className="border border-border rounded p-1.5 bg-cream-dark/40">
              <div className="flex items-center justify-between">
                <span className="pill bg-ink-950 text-cream text-[10px]">{k.id}</span>
                <span className="text-[10px] text-ink-500 tabular">half-life {k.decay_half_life_days}d</span>
              </div>
              <div className="text-ink-700 leading-snug mt-0.5">{k.description}</div>
            </li>
          ))}
        </ul>
        <div className="mt-3 text-[10.5px] text-ink-500">
          IRIs: {Object.entries(MEMORY_ONTOLOGY).slice(0, 4).map(([k, v]) => `${k}=${v}`).join(" · ")}…
        </div>
      </aside>
    </div>
  );
}
